// Co-driver voice (RALLY_GOAL.md §8): plays calls from the token sprite through a helmet-intercom
// chain (band-pass, saturation, compression, a little radio hiss) and ducks the engine under it.
// Falls back to the Web Speech API on the same tokens if the sprite can't load or decode.
import type { AudioMixer } from '../audio/mixer';
import { estimateDuration, type Call } from './caller';
import { COMPRESSED_RATE, scheduleTokens, spriteDuration, type SpriteManifest } from './sprite';

export type VoiceMode = 'sprite' | 'speech' | 'none';

const SPRITE_BASE = 'assets/audio/';

export class CodriverVoice {
  mode: VoiceMode = 'none';
  /** Why the fallback is active (reported in the debug overlay and the report). */
  fallbackReason = '';
  manifest: SpriteManifest | null = null;
  private buffer: AudioBuffer | null = null;
  private input: GainNode | null = null;
  private hiss: GainNode | null = null;
  private playing: AudioBufferSourceNode[] = [];
  private spriteDur: ((t: string[], c?: boolean) => number) | null = null;
  enabled = true;
  private readonly mixer: AudioMixer;
  private bytes: ArrayBuffer | null = null;

  constructor(mixer: AudioMixer) {
    this.mixer = mixer;
  }

  /** Fetch the sprite (with retries); decoding waits for the AudioContext (first user gesture). */
  async load(base = SPRITE_BASE): Promise<void> {
    try {
      this.manifest = (await fetchRetry(`${base}codriver.json`).then((r) => r.json())) as SpriteManifest;
      this.spriteDur = spriteDuration(this.manifest);
      this.bytes = await fetchRetry(`${base}${this.manifest.file}`).then((r) => r.arrayBuffer());
      this.mixer.onReady((ctx) => void this.decode(ctx));
    } catch (e) {
      this.useSpeech(`sprite failed to load (${String((e as Error)?.message ?? e)})`);
    }
  }

  private async decode(ctx: AudioContext): Promise<void> {
    if (!this.bytes) return;
    try {
      this.buffer = await ctx.decodeAudioData(this.bytes.slice(0));
      this.buildChain(ctx);
      this.mode = 'sprite';
    } catch (e) {
      this.useSpeech(`sprite failed to decode (${String((e as Error)?.message ?? e)})`);
    }
  }

  /** Web Audio could not start (no AudioContext): use the Web Speech fallback straight away. */
  noAudio(): void {
    if (this.mode === 'none') this.useSpeech('Web Audio unavailable');
  }

  private useSpeech(reason: string): void {
    this.fallbackReason = reason;
    this.mode = typeof speechSynthesis !== 'undefined' ? 'speech' : 'none';
    console.warn(`[codriver] ${reason}; using ${this.mode === 'speech' ? 'Web Speech' : 'subtitles only'}`);
  }

  /** Helmet intercom: HPF 320 Hz → LPF 3.4 kHz → presence peak → soft clip → compressor. */
  private buildChain(ctx: AudioContext): void {
    const input = ctx.createGain();
    input.gain.value = 1.1;
    const hp = ctx.createBiquadFilter();
    hp.type = 'highpass';
    hp.frequency.value = 320;
    hp.Q.value = 0.8;
    const lp = ctx.createBiquadFilter();
    lp.type = 'lowpass';
    lp.frequency.value = 3400;
    lp.Q.value = 0.9;
    const peak = ctx.createBiquadFilter();
    peak.type = 'peaking';
    peak.frequency.value = 1900;
    peak.Q.value = 1.1;
    peak.gain.value = 5;
    const shaper = ctx.createWaveShaper();
    const n = 1024;
    const curve = new Float32Array(n);
    const drive = 2.4;
    for (let i = 0; i < n; i++) {
      const x = (i / (n - 1)) * 2 - 1;
      curve[i] = Math.tanh(drive * x) / Math.tanh(drive);
    }
    shaper.curve = curve;
    shaper.oversample = '2x';
    const comp = ctx.createDynamicsCompressor();
    comp.threshold.value = -22;
    comp.ratio.value = 4;
    comp.attack.value = 0.004;
    comp.release.value = 0.12;
    const out = ctx.createGain();
    out.gain.value = 0.9;
    input.connect(hp).connect(lp).connect(peak).connect(shaper).connect(comp).connect(out).connect(this.mixer.buses.codriver);
    // Radio hiss bed, gated with the voice.
    const noise = ctx.createBuffer(1, ctx.sampleRate, ctx.sampleRate);
    const d = noise.getChannelData(0);
    let seed = 1234567;
    for (let i = 0; i < d.length; i++) {
      seed = (seed * 1103515245 + 12345) >>> 0;
      d[i] = (seed / 4294967296) * 2 - 1;
    }
    const src = ctx.createBufferSource();
    src.buffer = noise;
    src.loop = true;
    const bp = ctx.createBiquadFilter();
    bp.type = 'bandpass';
    bp.frequency.value = 2400;
    bp.Q.value = 0.6;
    const hiss = ctx.createGain();
    hiss.gain.value = 0;
    src.connect(bp).connect(hiss).connect(this.mixer.buses.codriver);
    src.start();
    this.input = input;
    this.hiss = hiss;
  }

  /** Spoken duration with the real clips (estimate until the sprite manifest is in). */
  durationOf = (tokens: string[], compressed = false): number => {
    if (this.spriteDur && this.mode !== 'speech') return this.spriteDur(tokens, compressed);
    return estimateDuration(tokens) / (compressed ? COMPRESSED_RATE : 1);
  };

  speak(call: Call): void {
    if (!this.enabled) return;
    if (this.mode === 'sprite' && this.buffer && this.input && this.mixer.ctx) {
      const ctx = this.mixer.ctx;
      if (ctx.state !== 'running') return;
      const rate = call.compressed ? COMPRESSED_RATE : 1;
      const { items, duration } = scheduleTokens(this.manifest!, call.tokens, rate);
      const t0 = ctx.currentTime + 0.02;
      for (const it of items) {
        const s = ctx.createBufferSource();
        s.buffer = this.buffer;
        s.playbackRate.value = rate;
        s.connect(this.input);
        s.start(t0 + it.at, it.offset, it.dur);
        s.onended = () => {
          const i = this.playing.indexOf(s);
          if (i >= 0) this.playing.splice(i, 1);
        };
        this.playing.push(s);
      }
      if (this.hiss) {
        const g = this.hiss.gain;
        g.cancelScheduledValues(t0);
        g.setTargetAtTime(0.012, t0 - 0.01, 0.01);
        g.setTargetAtTime(0, t0 + duration + 0.05, 0.03);
      }
      this.mixer.duck(true, t0 + duration);
    } else if (this.mode === 'speech') {
      try {
        const u = new SpeechSynthesisUtterance(call.tokens.join(' ').replace(/'/g, ''));
        u.rate = call.compressed ? 1.45 : 1.3;
        u.lang = 'en-GB';
        const v = speechSynthesis.getVoices().find((x) => x.lang === 'en-GB') ?? null;
        if (v) u.voice = v;
        speechSynthesis.speak(u);
      } catch {
        this.mode = 'none';
      }
    }
  }

  /** Stop any call in progress (pause, restart, reset). */
  stop(): void {
    for (const s of this.playing.splice(0)) {
      try {
        s.stop();
      } catch {
        /* already stopped */
      }
    }
    if (this.hiss && this.mixer.ctx) this.hiss.gain.setTargetAtTime(0, this.mixer.ctx.currentTime, 0.02);
    this.mixer.duck(false);
    if (this.mode === 'speech') {
      try {
        speechSynthesis.cancel();
      } catch {
        /* ignore */
      }
    }
  }
}

async function fetchRetry(url: string, tries = 3): Promise<Response> {
  let last: unknown;
  for (let i = 0; i < tries; i++) {
    try {
      const r = await fetch(url);
      if (r.ok) return r;
      last = new Error(`HTTP ${r.status}`);
    } catch (e) {
      last = e;
    }
    await new Promise((res) => setTimeout(res, 400 * (i + 1)));
  }
  throw last;
}
